import { apiRequest } from './apiClient';
import type { ChartViewMode } from '@/utils/chartRangeNormalizer';
import { normalizePaymentsChartRange } from '@/utils/chartRangeNormalizer';

type PaymentsStatsResponse = {
    total_cleared_transactions: number;
    total_cleared_amount: number;
    pending_transactions: number;
    open_transactions: number;
    low_balance_transactions: number;
};

type PaymentsStats = {
    totalClearedTransactions: number;
    totalClearedAmount: number;
    pendingTransactions: number;
    openTransactions: number;
    lowBalanceTransactions: number;
};

// PHASE1: total_accounts direct from API. Comment this and uncomment PHASE2 below to restore the verified/pending split.
export const getAccountsStats = async (from: string, to: string): Promise<{ totalAccounts: number }> => {
    const response = await apiRequest<{ total_accounts: number }>('/api/dashboard/stats/accounts', 'GET', {
        params: {
            from,
            to,
        },
    });

    return {
        totalAccounts: response.total_accounts,
    };
};

/*
 * PHASE2: Restore the verified/pending accounts breakdown.
export const getAccountsStats = async (from: string, to: string): Promise<{ verifiedAccounts: number, pendingAccounts: number }> => {
    const response = await apiRequest<{ verified_accounts: number, pending_accounts: number }>('/api/dashboard/stats/accounts', 'GET', {
        params: {
            from,
            to,
        },
    });

    return {
        verifiedAccounts: response.verified_accounts,
        pendingAccounts: response.pending_accounts,
    };
};
*/

export const getNegotiationsStats = async (from: string, to: string): Promise<{ eligibleAccounts: number, pendingOffers: number, acceptedOffers: number, rejectedOffers: number, completedNegotiations: number }> => {
    const response = await apiRequest<{ eligible_accounts: number, pending_offers: number, accepted_offers: number, rejected_offers: number, completed_negotiations: number }>('/api/dashboard/stats/negotiations', 'GET', {
        params: {
            from,
            to,
        },
    });

    return {
        eligibleAccounts: response.eligible_accounts,
        pendingOffers: response.pending_offers,
        acceptedOffers: response.accepted_offers,
        rejectedOffers: response.rejected_offers,
        completedNegotiations: response.completed_negotiations,
    };
};

export const getPaymentsStats = async (from: string, to: string): Promise<PaymentsStats> => {
    const response = await apiRequest<PaymentsStatsResponse>('/api/dashboard/stats/payments', 'GET', {
        params: {
            from,
            to,
        },
    });

    return {
        totalClearedTransactions: response.total_cleared_transactions,
        totalClearedAmount: response.total_cleared_amount,
        pendingTransactions: response.pending_transactions,
        openTransactions: response.open_transactions,
        lowBalanceTransactions: response.low_balance_transactions,
    };
};

export const getPaymentsChart = async (from: string, to: string, viewMode: ChartViewMode, tz?: string): Promise<{ labels: string[], values: (number|string)[], dailyAverage: number, from: string, to: string }> => {
    // PHASE1: Comment this line and uncomment the PHASE2 line to restore the previous chart window.
    const normalized = normalizePaymentsChartRange(from, to, viewMode, 20, tz);
    // PHASE2: const normalized = normalizePaymentsChartRange(from, to, viewMode, 10, tz);

    const response = await apiRequest<{ labels: string[], values: (number|string)[], daily_average: number }>('/api/dashboard/stats/payments-chart', 'GET', {
        params: {
            from: normalized.from,
            to: normalized.to,
            view: viewMode,
        },
    });

    return {
        labels: response.labels,
        values: response.values,
        dailyAverage: response.daily_average,
        from: normalized.from,
        to: normalized.to,
    };
};

export const getDailyPaymentAverage = async (from: string, to: string): Promise<number> => {
    const response = await apiRequest<{ daily_payment_average: number }>('/api/dashboard/stats/daily-payment-average', 'GET', {
        params: {
            from,
            to,
        },
    });

    return response.daily_payment_average;
};
